/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CustomerAttributeFilter } from '../models/CustomerAttributeFilter';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class MetricsTasksService {

    /**
     * Get opening approvals task count and URL
     * Returns count and URL for opening approvals pending the current user
     * @param xAccessToken
     * @param teamTasks
     * @param openingIds
     * @param locationIds
     * @param brandIds
     * @param positionIds
     * @param openingAttributes Filter opening approvals by customer attributes attached to the funnel itself. Encoded as opening_attributes[][attribute_key]=K&opening_attributes[][attribute_values][]=v.
     * @param positionAttributes Filter opening approvals by customer attributes attached to the funnel's position.
     * @param locationAttributes Filter opening approvals by customer attributes attached to the funnel's location.
     * @returns any OK
     * @throws ApiError
     */
    public static getInternalApiWxMetricsTasksOpeningApprovals(
        xAccessToken: string,
        teamTasks?: boolean,
        openingIds?: Array<string>,
        locationIds?: Array<string>,
        brandIds?: Array<string>,
        positionIds?: Array<string>,
        openingAttributes?: Array<CustomerAttributeFilter>,
        positionAttributes?: Array<CustomerAttributeFilter>,
        locationAttributes?: Array<CustomerAttributeFilter>,
    ): CancelablePromise<{
        tasks?: {
            opening_approvals?: {
                applicants_count?: number;
                review_url?: string;
            };
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/wx/metrics/tasks/opening_approvals',
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
            query: {
                'team_tasks': teamTasks,
                'opening_ids[]': openingIds,
                'location_ids[]': locationIds,
                'brand_ids[]': brandIds,
                'position_ids[]': positionIds,
                'opening_attributes[]': openingAttributes,
                'position_attributes[]': positionAttributes,
                'location_attributes[]': locationAttributes,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
    }

    /**
     * Get review files task count and URL
     * Returns count and URL for files pending review by the current user
     * @param xAccessToken
     * @param teamTasks
     * @param openingIds
     * @param locationIds
     * @param brandIds
     * @param positionIds
     * @param openingAttributes Filter review files by customer attributes attached to the funnel itself. Encoded as opening_attributes[][attribute_key]=K&opening_attributes[][attribute_values][]=v.
     * @param positionAttributes Filter review files by customer attributes attached to the funnel's position.
     * @param locationAttributes Filter review files by customer attributes attached to the funnel's location.
     * @returns any OK
     * @throws ApiError
     */
    public static getInternalApiWxMetricsTasksReviewFiles(
        xAccessToken: string,
        teamTasks?: boolean,
        openingIds?: Array<string>,
        locationIds?: Array<string>,
        brandIds?: Array<string>,
        positionIds?: Array<string>,
        openingAttributes?: Array<CustomerAttributeFilter>,
        positionAttributes?: Array<CustomerAttributeFilter>,
        locationAttributes?: Array<CustomerAttributeFilter>,
    ): CancelablePromise<{
        tasks?: {
            review_files?: {
                applicants_count?: number;
                review_url?: string;
            };
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/wx/metrics/tasks/review_files',
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
            query: {
                'team_tasks': teamTasks,
                'opening_ids[]': openingIds,
                'location_ids[]': locationIds,
                'brand_ids[]': brandIds,
                'position_ids[]': positionIds,
                'opening_attributes[]': openingAttributes,
                'position_attributes[]': positionAttributes,
                'location_attributes[]': locationAttributes,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
    }

    /**
     * Get mark attendance task count and URL
     * Returns count and URL for attendance the current user needs to mark
     * @param xAccessToken
     * @param teamTasks
     * @param openingIds
     * @param locationIds
     * @param brandIds
     * @param positionIds
     * @param openingAttributes Filter mark attendance by customer attributes attached to the funnel itself. Encoded as opening_attributes[][attribute_key]=K&opening_attributes[][attribute_values][]=v.
     * @param positionAttributes Filter mark attendance by customer attributes attached to the funnel's position.
     * @param locationAttributes Filter mark attendance by customer attributes attached to the funnel's location.
     * @returns any OK
     * @throws ApiError
     */
    public static getInternalApiWxMetricsTasksMarkAttendance(
        xAccessToken: string,
        teamTasks?: boolean,
        openingIds?: Array<string>,
        locationIds?: Array<string>,
        brandIds?: Array<string>,
        positionIds?: Array<string>,
        openingAttributes?: Array<CustomerAttributeFilter>,
        positionAttributes?: Array<CustomerAttributeFilter>,
        locationAttributes?: Array<CustomerAttributeFilter>,
    ): CancelablePromise<{
        tasks?: {
            mark_attendance?: {
                applicants_count?: number;
                review_url?: string;
            };
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/wx/metrics/tasks/mark_attendance',
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
            query: {
                'team_tasks': teamTasks,
                'opening_ids[]': openingIds,
                'location_ids[]': locationIds,
                'brand_ids[]': brandIds,
                'position_ids[]': positionIds,
                'opening_attributes[]': openingAttributes,
                'position_attributes[]': positionAttributes,
                'location_attributes[]': locationAttributes,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
    }

    /**
     * Get respond to messages task count and URL
     * Returns count and URL for messages the current user needs to respond to
     * @param xAccessToken
     * @param teamTasks
     * @param openingIds
     * @param locationIds
     * @param brandIds
     * @param positionIds
     * @param openingAttributes Filter respond to messages by customer attributes attached to the funnel itself. Encoded as opening_attributes[][attribute_key]=K&opening_attributes[][attribute_values][]=v.
     * @param positionAttributes Filter respond to messages by customer attributes attached to the funnel's position.
     * @param locationAttributes Filter respond to messages by customer attributes attached to the funnel's location.
     * @returns any OK
     * @throws ApiError
     */
    public static getInternalApiWxMetricsTasksRespondToMessages(
        xAccessToken: string,
        teamTasks?: boolean,
        openingIds?: Array<string>,
        locationIds?: Array<string>,
        brandIds?: Array<string>,
        positionIds?: Array<string>,
        openingAttributes?: Array<CustomerAttributeFilter>,
        positionAttributes?: Array<CustomerAttributeFilter>,
        locationAttributes?: Array<CustomerAttributeFilter>,
    ): CancelablePromise<{
        tasks?: {
            respond_to_messages?: {
                applicants_count?: number;
                review_url?: string;
            };
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/wx/metrics/tasks/respond_to_messages',
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
            query: {
                'team_tasks': teamTasks,
                'opening_ids[]': openingIds,
                'location_ids[]': locationIds,
                'brand_ids[]': brandIds,
                'position_ids[]': positionIds,
                'opening_attributes[]': openingAttributes,
                'position_attributes[]': positionAttributes,
                'location_attributes[]': locationAttributes,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
    }

    /**
     * Get review applicants task count and URL
     * Returns count and URL for applicants pending review by the current user
     * @param xAccessToken
     * @param teamTasks
     * @param openingIds
     * @param locationIds
     * @param brandIds
     * @param positionIds
     * @param openingAttributes Filter review applicants by customer attributes attached to the funnel itself. Encoded as opening_attributes[][attribute_key]=K&opening_attributes[][attribute_values][]=v.
     * @param positionAttributes Filter review applicants by customer attributes attached to the funnel's position.
     * @param locationAttributes Filter review applicants by customer attributes attached to the funnel's location.
     * @returns any OK
     * @throws ApiError
     */
    public static getInternalApiWxMetricsTasksReviewApplicants(
        xAccessToken: string,
        teamTasks?: boolean,
        openingIds?: Array<string>,
        locationIds?: Array<string>,
        brandIds?: Array<string>,
        positionIds?: Array<string>,
        openingAttributes?: Array<CustomerAttributeFilter>,
        positionAttributes?: Array<CustomerAttributeFilter>,
        locationAttributes?: Array<CustomerAttributeFilter>,
    ): CancelablePromise<{
        tasks?: {
            review_applicants?: {
                applicants_count?: number;
                review_url?: string;
            };
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/wx/metrics/tasks/review_applicants',
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
            query: {
                'team_tasks': teamTasks,
                'opening_ids[]': openingIds,
                'location_ids[]': locationIds,
                'brand_ids[]': brandIds,
                'position_ids[]': positionIds,
                'opening_attributes[]': openingAttributes,
                'position_attributes[]': positionAttributes,
                'location_attributes[]': locationAttributes,
            },
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
    }

}